import {
  AloftPrice,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectOrderHasExternalDepreciation } from '../selectOrderDepreciation.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { priceJSONToPrice } from '../utils/priceJSONToPrice.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateExternalObsolescenceAdj = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  orderHasExternalDepreciation: boolean,
  compID: string,
  salesComparisonApproach: SalesComparisonApproachType,
) => {
  // No unit-rate adjustments for external obsolescence
  if (salesComparisonApproach === 'unit_rate') return undefined;

  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];
  if (!orderHasExternalDepreciation || !subjectImprovements || !compImprovements) return undefined;

  const { externalDepreciation } = subjectImprovements;
  const { externalDepreciation: compExternalDepreciation } = compImprovements;

  const externalDepreciationParsed = priceJSONToPrice(externalDepreciation) ?? AloftPrice.zero;
  const compExternalDepreciationParsed =
    priceJSONToPrice(compExternalDepreciation) ?? AloftPrice.zero;

  // Comp - Subject, since the depreciation values are stored as positive values
  return compExternalDepreciationParsed.sub(externalDepreciationParsed);
};

const selectExternalObsolescenceAdj = createPriceSelector(
  [
    selectImprovementsEntities,
    selectOrderHasExternalDepreciation,
    (_state, id: string) => id,
    selectSalesComparisonApproach,
  ],
  calculateExternalObsolescenceAdj,
);

export const selectExternalObsolescenceAdjs = createSelector(
  [
    selectImprovementsEntities,
    selectOrderHasExternalDepreciation,
    selectCompSequence,
    selectSalesComparisonApproach,
  ],
  (improvements, orderHasExternalDepreciation, compSequence, salesComparisonApproach) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateExternalObsolescenceAdj(
        improvements,
        orderHasExternalDepreciation,
        propID,
        salesComparisonApproach,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedExternalObsolescenceAdj = createSelector(
  [selectExternalObsolescenceAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'externalObsolescence', adjSettings),
);

export const selectAppliedExternalObsolescenceAdjDisplay = createSelector(
  [selectAppliedExternalObsolescenceAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedExternalObsolescenceAdjsDisplay = createSelector(
  [selectCompSequence, selectExternalObsolescenceAdjs, selectAdjustmentSettings],
  (compSequence, conditionAdjs, adjSettings) =>
    compSequence.reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = conditionAdjs[propID];

      if (adj) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adj, 'externalObsolescence', adjSettings),
          ),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {}),
);
